import { SelectableContainer } from '@acies/core';
import CheckBoxEvent from './check-box-event';
import Icon from './icon';
import theme from './theme';

/**
 * The variant both icons of the CheckBox are drawn in, state by state.
 *
 * Keyed by the styled state rather than the state, so hovering has no look of its own: the cursor is what says the CheckBox can be checked. Written out per state, so the compiler asks for a variant the day a state is added.
 */
const VARIANTS: Readonly<Record<CheckBox<unknown>['styledState'], Icon['variant']>> = {
	disabled: 'disabled',
	focused: 'focused',
	focusedPressed: 'default',
	idle: 'default',
	pressed: 'default',
};

/**
 * The CheckBox is used in many different UI components as a child.
 *
 * The CheckBox dispatches a CheckBoxEvent when the user selects or deselects the CheckBox.
 *
 * You can provide a item value that will be sent along with the CheckBoxEvent as the item property.
 */
export default class CheckBox<Item> extends SelectableContainer<Item> {
	/**
	 * The item value will be sent along with the CheckBoxEvent as the item property.
	 *
	 * @param {Item | null} value The item value
	 */
	public set item(value: Item | null) {
		this.#item = value;
	}

	public get item(): Item | null {
		return this.#item;
	}

	#checkChild!: Icon;
	#item: Item | null;
	#squareChild!: Icon;

	/** CheckChild renders the check symbol inside the square and toggles visibility when the selected property changes. */
	private get checkChild(): Icon {
		if (!this.#checkChild) {
			this.#checkChild = new Icon('check-square');
			// the default icon size is 18px, but in this case,
			// we use it as actual UI, so we set the size to 24px.
			this.#checkChild.width = 20;
			this.#checkChild.height = 20;
			this.#checkChild.fill = theme.colors.fill.white;
			this.#checkChild.position = 'absolute';
			this.#checkChild.visible = false;
		}
		return this.#checkChild;
	}

	/** SquareChild renders the empty square. */
	private get squareChild(): Icon {
		if (!this.#squareChild) {
			this.#squareChild = new Icon('square');
			this.#squareChild.width = 20;
			this.#squareChild.height = 20;
			this.#squareChild.fill = theme.colors.fill.white;
			this.#squareChild.position = 'absolute';
		}
		return this.#squareChild;
	}

	public constructor() {
		super();
		this.#item = null;
		this.alignment = 'center';
		this.cursor = 'pointer';
		this.height = 15;
		this.width = 15;
	}

	/** Since the CheckBox could be a child of a SelectableContainer, we don't call super.clicked(), but stop the Event from going up the callstack so the parent wont get selected aswell. */
	protected override clicked(event: Event): void {
		event.stopImmediatePropagation();
		this.selected = !this.selected;
		this.dispatchEvent(new CheckBoxEvent(this.selected, this.item));
	}

	protected override enabledChanged(): void {
		super.enabledChanged();
		if (this.enabled) {
			this.cursor = 'pointer';
		} else {
			this.cursor = 'default';
		}
		this.#invalidateStyles();
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.squareChild, this.checkChild]);
	}

	protected override selectedChanged(): void {
		super.selectedChanged();
		// simply toggle the visibility of the 'checked' icon
		this.checkChild.visible = this.selected;
	}

	protected override stateChanged(): void {
		super.stateChanged();
		this.#invalidateStyles();
	}

	#invalidateStyles(): void {
		const variant = VARIANTS[this.styledState];
		this.checkChild.variant = variant;
		this.squareChild.variant = variant;
	}
}
customElements.define('ac-check-box', CheckBox);
